<template>
  <button
    :type="type"
    :disabled="disabled"
    :class="classes"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
  /**
   * Button — Tailwind primitive replacing BootstrapVue-Next's `b-btn` /
   * `b-button`.
   *
   * Visual baseline matches the existing BVN button defaults so swap-in
   * PRs don't introduce surprise redesigns. Aesthetic refinements should
   * land in a separate, intentional pass.
   *
   * Usage:
   *   <UiButton>Click me</UiButton>
   *   <UiButton variant="success" size="sm" @click="save">Save</UiButton>
   *   <UiButton variant="link" :disabled="loading">Show details</UiButton>
   */
  import { computed } from 'vue'

  defineOptions({ name: 'UiButton' })

  interface Props {
    variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'link' | 'ghost'
    size?: 'sm' | 'md' | 'lg'
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    variant: 'primary',
    size: 'md',
    type: 'button',
    disabled: false,
  })

  // Filled variants share a hover/active darken pattern via brightness; the
  // semantic colors (secondary/success/danger/warning/info) come from
  // tailwind.config.js theme tokens defined for this app.
  const VARIANTS: Record<NonNullable<Props['variant']>, string> = {
    primary:   'tw-bg-primary-600 tw-text-white tw-border-primary-600 hover:tw-bg-primary-700 hover:tw-border-primary-700 active:tw-bg-primary-800 disabled:tw-bg-primary-300 disabled:tw-border-primary-300',
    secondary: 'tw-bg-secondary tw-text-white tw-border-secondary hover:tw-brightness-95 active:tw-brightness-90',
    success:   'tw-bg-success tw-text-white tw-border-success hover:tw-brightness-95 active:tw-brightness-90',
    danger:    'tw-bg-danger tw-text-white tw-border-danger hover:tw-brightness-95 active:tw-brightness-90',
    warning:   'tw-bg-warning tw-text-gray-900 tw-border-warning hover:tw-brightness-95 active:tw-brightness-90',
    info:      'tw-bg-info tw-text-white tw-border-info hover:tw-brightness-95 active:tw-brightness-90',
    link:      'tw-bg-transparent tw-text-primary-600 tw-border-transparent hover:tw-underline hover:tw-text-primary-700',
    ghost:     'tw-bg-transparent tw-text-gray-700 tw-border-transparent hover:tw-bg-gray-100',
  }

  // Sizes track Bootstrap's default / .btn-sm / .btn-lg dimensions
  const SIZES: Record<NonNullable<Props['size']>, string> = {
    sm: 'tw-px-2 tw-py-1 tw-text-sm tw-leading-tight tw-rounded',
    md: 'tw-px-3 tw-py-1.5 tw-text-base tw-leading-normal tw-rounded-md',
    lg: 'tw-px-4 tw-py-2 tw-text-lg tw-leading-normal tw-rounded-lg',
  }

  const BASE = [
    'tw-inline-flex tw-items-center tw-justify-center',
    'tw-font-normal tw-whitespace-nowrap tw-align-middle',
    'tw-border tw-border-solid',
    'tw-cursor-pointer tw-select-none',
    'tw-transition-colors tw-duration-150',
    'focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-300',
    'disabled:tw-cursor-not-allowed disabled:tw-opacity-65',
  ].join(' ')

  const classes = computed(() => [
    BASE,
    VARIANTS[props.variant],
    SIZES[props.size],
  ])
</script>
